import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  Checkbox,
  createFilterOptions,
  FilterOptionsState,
  FormControlLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { FormLabelText } from '../../../components/Form/FormComponents';

export interface IPickerOption<T> {
  key: string;
  value: T;
  label: string;
  group: string;
}

interface IGroupedCheckboxPickerProps<T> {
  /** Names the search box and the picker as a whole. */
  label: string;
  /** Every option in picker order, the order newly checked values join the selection in. */
  options: IPickerOption<T>[];
  /** The checked values, in their own order. */
  selected: T[];
  keyOf: (value: T) => string;
  onChange: (next: T[]) => void;
  /** Shown left of Select all instead of the label text. */
  heading?: ReactNode;
  noOptionsText?: string;
}

const getOptionLabel = <T,>(option: IPickerOption<T>) => option.label;
const checkboxSx = { p: 0, mr: '8px' };
// the group line keeps the list's left edge; its options sit three spacing units in, under the group's
// name (a margin on the checkbox, as the popup's own option padding outranks a row style)
const optionCheckboxSx = { ...checkboxSx, ml: 3 };

interface ISelectionState {
  count: number;
  all: boolean;
  some: boolean;
}

/**
 * A checkbox multi-select whose checked state is a list: checking appends, unchecking removes, and only
 * the option that was clicked changes, so a selected value the options no longer offer stays put. Select
 * all on the heading line and each group's checkbox act on what the search currently matches.
 */
export function GroupedCheckboxPicker<T>({
  label,
  options,
  selected,
  keyOf,
  onChange,
  heading,
  noOptionsText = 'No matches',
}: IGroupedCheckboxPickerProps<T>) {
  const [inputValue, setInputValue] = useState('');

  // names match as they do in every select; from two characters on, a word of a group's name lists the group
  const byName = useMemo(() => createFilterOptions<IPickerOption<T>>(), []);
  const filterOptions = useCallback(
    (candidates: IPickerOption<T>[], state: FilterOptionsState<IPickerOption<T>>) => {
      const query = state.inputValue.trim().toLowerCase();
      const nameMatches = new Set(byName(candidates, state));
      const groupMatches = (group: string) =>
        query.length >= 2 &&
        group
          .toLowerCase()
          .split(/\s+/)
          .some((word) => word.startsWith(query));
      return candidates.filter((option) => nameMatches.has(option) || groupMatches(option.group));
    },
    [byName]
  );

  const selectedKeys = useMemo(() => new Set(selected.map(keyOf)), [keyOf, selected]);
  const value = useMemo(
    () => options.filter((option) => selectedKeys.has(option.key)),
    [options, selectedKeys]
  );
  const visible = useMemo(
    () => filterOptions(options, { inputValue, getOptionLabel }),
    [filterOptions, inputValue, options]
  );

  const selectionOf = useCallback(
    (subset: IPickerOption<T>[]): ISelectionState => {
      const count = subset.filter((option) => selectedKeys.has(option.key)).length;
      return {
        count,
        all: subset.length > 0 && count === subset.length,
        some: count > 0 && count < subset.length,
      };
    },
    [selectedKeys]
  );

  // Kept values keep their place; new ones go on the end, in picker order.
  const toggle = useCallback(
    (subset: IPickerOption<T>[]) => {
      const keys = new Set(selectedKeys);
      const { all } = selectionOf(subset);
      subset.forEach((option) => (all ? keys.delete(option.key) : keys.add(option.key)));
      const kept = selected.filter((item) => keys.has(keyOf(item)));
      const keptKeys = new Set(kept.map(keyOf));
      const added = options.filter((option) => keys.has(option.key) && !keptKeys.has(option.key));
      onChange([...kept, ...added.map((option) => option.value)]);
    },
    [keyOf, onChange, options, selected, selectedKeys, selectionOf]
  );

  const visibleSelection = selectionOf(visible);

  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    const groupOptions = visible.filter((option) => option.group === params.group);
    const groupSelection = selectionOf(groupOptions);
    return (
      <li key={params.key}>
        <ListSubheader
          component='div'
          className={autocompleteClasses.groupLabel}
          // top cancels the popup list's 8px top padding, so the sticky group line hugs the top
          sx={{ display: 'flex', alignItems: 'center', top: -8, lineHeight: '2.25rem' }}
        >
          <Checkbox
            size='small'
            sx={checkboxSx}
            checked={groupSelection.all}
            indeterminate={groupSelection.some}
            onChange={() => toggle(groupOptions)}
            inputProps={{ 'aria-label': `Select all ${params.group}` }}
          />
          <Typography component='span' variant='subtitle2' color='text.primary'>
            {params.group}
          </Typography>
          <Typography component='span' variant='caption' color='text.secondary' ml='auto'>
            {`${groupSelection.count} of ${groupOptions.length}`}
          </Typography>
        </ListSubheader>
        <ul className={autocompleteClasses.groupUl} style={{ padding: 0 }}>
          {params.children}
        </ul>
      </li>
    );
  };

  return (
    <Stack gap='0.25rem' role='group' aria-label={label}>
      {/* above the search box, where the popup cannot cover it */}
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        gap='1rem'
        // a click here leaves the input focused, so an open popup stays open
        onMouseDown={(event) => event.preventDefault()}
      >
        {heading ?? <FormLabelText label={label} />}
        <Stack direction='row' alignItems='center' gap='0.5rem'>
          <FormControlLabel
            sx={{ m: 0 }}
            slotProps={{ typography: { variant: 'body2' } }}
            label='Select all'
            control={
              <Checkbox
                size='small'
                sx={checkboxSx}
                checked={visibleSelection.all}
                indeterminate={visibleSelection.some}
                disabled={visible.length === 0}
                onChange={() => toggle(visible)}
              />
            }
          />
          <Typography variant='caption' color='text.secondary'>
            {`${visibleSelection.count} of ${visible.length}${inputValue ? ' matching' : ''}`}
          </Typography>
        </Stack>
      </Stack>
      <Autocomplete
        aria-label={label}
        multiple
        disableCloseOnSelect
        options={options}
        value={value}
        // only the option that was clicked or chosen by keyboard changes; the clear button is left to the text
        onChange={(_, __, reason, details) => {
          if (details && (reason === 'selectOption' || reason === 'removeOption')) {
            toggle([details.option]);
          }
        }}
        inputValue={inputValue}
        // typed and cleared by the user only: MUI also empties the text after each pick and on blur,
        // and keeping it lets several matches be ticked in a row
        onInputChange={(_, next, reason) => {
          if (reason === 'input' || reason === 'clear') {
            setInputValue(next);
          }
        }}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        // two options may share a label; the key is what tells them apart
        getOptionKey={(option) => option.key}
        groupBy={(option) => option.group}
        isOptionEqualToValue={(option, selectedOption) => option.key === selectedOption.key}
        noOptionsText={noOptionsText}
        renderGroup={renderGroup}
        renderOption={(props, option, { selected: isSelected }) => {
          const { key, ...optionProps } = props;
          return (
            <MenuItem key={key} {...optionProps}>
              <Checkbox checked={isSelected} size='small' sx={optionCheckboxSx} />
              <ListItemText primary={option.label} sx={{ m: 0 }} />
            </MenuItem>
          );
        }}
        // the list under the picker shows the selection, so the input stays a search box
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Search'
            slotProps={{ htmlInput: { ...params.inputProps, 'aria-label': label } }}
            onKeyDown={(event) => {
              // Backspace in an empty box would otherwise uncheck the last option, as it removes a tag
              if (event.key === 'Backspace' && (event.target as HTMLInputElement).value === '') {
                event.stopPropagation();
              }
            }}
          />
        )}
      />
    </Stack>
  );
}
